import React from "react";
import { ArrowRight, MoveDown } from "lucide-react";

const HERO_IMG = "/hero-bg.jpg";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-end pb-16 md:pb-20 overflow-hidden"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Bespoke event bar — 3D visualisation at twilight"
                    className="w-full h-full object-cover"
                    loading="eager"
                />
                {/* Left-to-right gradient — lighter overall to let the image breathe */}
                <div className="absolute inset-0 bg-gradient-to-r from-background/75 via-background/35 to-transparent" />
                {/* Bottom fade into the next section */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
            </div>

            {/* Top-line metadata bar */}
            <div className="absolute top-24 left-0 right-0">
                <div className="container-edge flex items-center justify-end">
                    <p className="overline hidden md:block">
                        3D Event CAD · Spatial Visualisation
                    </p>
                </div>
            </div>

            {/* Center brand line */}
            <div className="container-edge relative">
                <p className="overline mb-6">
                    <span className="inline-block w-8 h-px bg-foreground/40 align-middle mr-3" />
                    3D Event CAD & Spatial Visualisation
                </p>

                <h1
                    data-testid="hero-headline"
                    className="font-display font-medium text-balance text-[44px] sm:text-[64px] md:text-[88px] lg:text-[112px] leading-[0.92] tracking-[-0.03em] max-w-[1100px]"
                    style={{
                        textShadow:
                            "0 0 24px hsl(var(--background) / 0.85), 0 0 60px hsl(var(--background) / 0.55)",
                    }}
                >
                    Temporary Spaces.
                    <br />
                    <span className="italic font-normal text-foreground">Built With Purpose.</span>
                </h1>

                <div className="mt-10 max-w-2xl">
                    <p
                        className="text-foreground/85 text-base md:text-lg leading-relaxed font-light"
                        style={{
                            textShadow:
                                "0 0 14px hsl(var(--background) / 0.95), 0 0 28px hsl(var(--background) / 0.7)",
                        }}
                    >
                        I create clear, accurate and production-aware 3D visuals for
                        events, sets and temporary environments — helping agencies,
                        planners, brands and production teams understand the space,
                        approve the concept and move towards build with confidence.
                    </p>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <a
                        href="#contact"
                        data-testid="hero-cta-start-project"
                        className="btn-primary"
                    >
                        Start a Project <ArrowRight size={16} />
                    </a>
                    <a
                        href="#services"
                        data-testid="hero-cta-view-services"
                        className="btn-secondary"
                    >
                        View Services
                    </a>
                    <a
                        href="#what-i-create"
                        data-testid="hero-cta-see-what"
                        className="btn-ghost"
                    >
                        See What I Create →
                    </a>
                </div>
            </div>

            {/* Subtle scroll prompt */}
            <div className="container-edge mt-16 md:mt-24 relative">
                <a
                    href="#what-i-do"
                    data-testid="hero-scroll-prompt"
                    className="group inline-flex items-center gap-4 text-foreground/60 hover:text-foreground transition-colors"
                >
                    <span className="overline">Scroll to continue</span>
                    <span className="block w-12 h-px bg-current group-hover:w-20 transition-all duration-500" />
                    <MoveDown size={14} className="transition-transform duration-500 group-hover:translate-y-1" />
                </a>
            </div>
        </section>
    );
}
